import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import axios from '../../../api/axios';
import Header from '../layout/Header';
import { JOB_TYPE_OPTIONS, POSITION_OPTIONS, getJobTypeString, getPositionString } from '../../../utils/Constants';
import './CandidateForm.css';
import { User, QuestionTemplate } from '../../../types';
import { validateEmail, containsEmoji } from '../../../utils/Utils';
import { ApiError } from '../../../types/errors';
import QuestionTemplateSelectionModal from '../common/QuestionTemplateSelectionModal';

interface FormData {
  name: string;
  email: string;
  job_type: number;
  position: number;
  assignment_location: string;
  question_template_id: number;
  internal_memo: string;
  [key: string]: string | number;
}

interface FormErrors {
  name?: string;
  email?: string;
  [key: string]: string | undefined;
}

interface CandidateData extends FormData {
  user_id: string;
  assignment_location: string;
}


const CandidateForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    job_type: 0,
    position: 0,
    assignment_location: '',
    question_template_id: 0,
    internal_memo: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [questionTemplates, setQuestionTemplates] = useState<QuestionTemplate[]>([]);
  const [showQuestionTemplateModal, setShowQuestionTemplateModal] = useState<boolean>(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setUser(userData);
      } catch (error: unknown) {
        console.error('Error parsing user data:', error);
        navigate('/');
      }
    } else {
      navigate('/');
    }

    const fetchQuestionTemplates = async (): Promise<void> => {
      try {
        const response = await axios.get('/question-templates', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuestionTemplates(response.data);
      } catch (error: unknown) {
        console.error('Error fetching question templates:', error);
      }
    };

    fetchQuestionTemplates();

    if (isEditMode) {
      const fetchCandidate = async (): Promise<void> => {
        try {
          const response = await axios.get(`/candidates/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const candidateData = {
            ...response.data,
          };
          setFormData(candidateData);
        } catch (error: unknown) {
          const axiosError = error as ApiError;
          console.error('Error fetching candidate:', error);
        }
      };

      fetchCandidate();
    }
  }, [id, isEditMode, navigate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isSameWithCompany = (candidateEmail: string): boolean => {
    candidateEmail.trim();
    return candidateEmail.toLowerCase() === user?.email.toLowerCase();
  }

  const validateForm = async (): Promise<FormErrors> => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) newErrors.name = '氏名を入力してください。';
    else if (containsEmoji(formData.name)) newErrors.name = '絵文字は使用できません。';

    if (!formData.email.trim()) newErrors.email = 'メールアドレスを入力してください。';
    else if (!validateEmail(formData.email)) newErrors.email = '不正なメールアドレスです。';
    else if (isSameWithCompany(formData.email)) newErrors.email = '企業側のメールアドレスです。';
    else if (containsEmoji(formData.email)) newErrors.email = '絵文字は使用できません。';

    if (formData.assignment_location && containsEmoji(formData.assignment_location)) {
      newErrors.assignment_location = '絵文字は使用できません。';
    }

    if (!formData.question_template_id || formData.question_template_id === 0) {
      newErrors.question_template_id = '質問テンプレートを選択してください。';
    }

    if (formData.internal_memo && containsEmoji(formData.internal_memo)) {
      newErrors.internal_memo = '絵文字は使用できません。';
    }

    // 候補者メールアドレスの重複チェック
    if (!newErrors.email) {
      try {
        const token = localStorage.getItem('token');
        const checkUrl = isEditMode 
          ? `/candidates/check-email?email=${encodeURIComponent(formData.email)}&exclude=${id}`
          : `/candidates/check-email?email=${encodeURIComponent(formData.email)}`;

        const response = await axios.get(checkUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.exists) {
          newErrors.email = '既に登録済みのメールアドレスです。';
        }
      } catch (error: unknown) {
        const axiosError = error as ApiError;
        console.error('Email check error:', axiosError);
        newErrors.email = 'メールアドレスの重複チェック中にエラーが発生しました。もう一度お試しください。';
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (e.type === 'submit') {
      e.preventDefault();
    }

    const newErrors = await validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setShowConfirmModal(true);
    setErrors({});
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user') || '{}';
      const userData = JSON.parse(userStr);

      const candidateData: CandidateData = {
        ...formData,
        user_id: userData.id,
      };

      if (isEditMode) {
        const response = await axios.put(`/candidates/${id}`, candidateData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setShowConfirmModal(false);
        alert('候補者情報の更新に成功しました。');
      } else {
        const response = await axios.post('/candidates', candidateData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setShowConfirmModal(false);
        alert('候補者の作成に成功しました。');
        navigate('/dashboard');
      }
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error saving candidate:', axiosError);
      console.error('Error details:', axiosError.response ? axiosError.response.data : 'No response data');

      if (axiosError.response?.data?.errors) {
        const responseErrors = axiosError.response.data.errors;
        const formattedErrors: FormErrors = {};

        if (responseErrors.name) formattedErrors.name = responseErrors.name;
        if (responseErrors.email) formattedErrors.email = responseErrors.email;

        setErrors(formattedErrors);
      } else if (isEditMode) {
        alert('保存中にエラーが発生しました。もう一度お試しください。');
      } else {
        alert('候補者の作成に失敗しました。管理者にお問い合わせください。');
      }

      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuestionTemplateSelect = (templateId: number): void => {
    setFormData(prev => ({ ...prev, question_template_id: templateId }));
    setErrors(prev => ({ ...prev, question_template_id: undefined }));
  };

  return (
    <div className="candidate-form-container">
      <Header user={user} />

      <Container>
        <h4 className="form-title">リファレンスチェック新規作成</h4>

        <Row>
          <Col md={8}>
            <div className="form-container">
              <div className="form-header">
                候補者情報
              </div>
              <div className="form-body">
                <div className="respondent-section">
                  <Form onSubmit={handleSubmit}>
                    <Row className="form-row">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">氏名 <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            isInvalid={!!errors.name}
                            placeholder="（例）山田太郎"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">メールアドレス <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder="（例）xxxxxxxxxxxx@xxxxxxx.com"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="form-label">職種</Form.Label>
                          <Form.Select
                            name="job_type"
                            value={formData.job_type}
                            onChange={handleChange}
                            className="form-select"
                          >
                            <option value="">選択してください</option>
                            {JOB_TYPE_OPTIONS.map((option) => (
                              <option key={option.code} value={option.code}>
                                {option.text}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.job_type}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="form-label">職位</Form.Label>
                          <Form.Select
                            name="position"
                            value={formData.position}
                            onChange={handleChange}
                            className="form-select"
                          >
                            <option value="">選択してください</option>
                            {POSITION_OPTIONS.map((option) => (
                              <option key={option.code} value={option.code}>
                                {option.text}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.position}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="form-label">配属先</Form.Label>
                          <Form.Control
                            type="text"
                            name="assignment_location"
                            value={formData.assignment_location}
                            onChange={handleChange}
                            isInvalid={!!errors.assignment_location}
                            placeholder="配属先を入力してください"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.assignment_location}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="form-label">質問選択 <span className="required-mark">*</span></Form.Label>
                          <div className="question-template-selection">
                            <Button
                              variant="outline-secondary"
                              className="question-template-button"
                              onClick={() => setShowQuestionTemplateModal(true)}
                              style={{ 
                                border: errors.question_template_id ? '1px solid #dc3545' : '1px solid #ced4da'
                              }}
                            >
                              {formData.question_template_id 
                                ? questionTemplates.find(t => t.id === Number(formData.question_template_id))?.name || '選択してください'
                                : '選択してください'
                              }
                            </Button>
                            {errors.question_template_id && (
                              <div className="invalid-feedback" style={{ display: 'block' }}>
                                {errors.question_template_id}
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="form-label">社内向けメモ</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="internal_memo"
                            value={formData.internal_memo}
                            onChange={handleChange}
                            isInvalid={!!errors.internal_memo}
                            placeholder="（例）1次面接済み。ストレス耐性に懸念があるのでリファレンスチェックをしたい。"
                            className="form-textarea"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.internal_memo}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="confirmation-button-container">
              <Button 
                variant="success"
                className="submit-button" 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                確認画面へ
              </Button>
              <p className="note-text text-left">必須項目を入力してください</p>
            </div>
          </Col>
        </Row>

        <Link to="/dashboard" className="back-link">
          <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
          トップへ戻る
        </Link>

        {/* Confirmation Modal */}
        <Modal
          show={showConfirmModal} 
          onHide={() => setShowConfirmModal(false)}
          centered
          className="candidate-confirm-modal"
          backdrop="static"  // Prevent close when clicking outside
          keyboard={false}   // Optional: Prevent closing with ESC key
        >
          <Modal.Header closeButton>
            <Modal.Title>候補者情報確認</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">氏名</div>
                <div className="candidate-detail-value">{formData.name}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">メールアドレス</div>
                <div className="candidate-detail-value">{formData.email}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職種</div>
                <div className="candidate-detail-value">{getJobTypeString(Number(formData.job_type)) || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">職位</div>
                <div className="candidate-detail-value">{getPositionString(Number(formData.position)) || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">配属先</div>
                <div className="candidate-detail-value">{formData.assignment_location || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">質問選択</div>
                <div className="candidate-detail-value">
                  {questionTemplates.find(t => t.id === Number(formData.question_template_id))?.name || '-'}
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">社内向けメモ</div>
                <div className="candidate-detail-value internal-memo">{formData.internal_memo || '-'}</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="success" 
              className="save-button" 
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
            >
              作成
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Question Template Selection Modal */}
        <QuestionTemplateSelectionModal
          show={showQuestionTemplateModal}
          onHide={() => setShowQuestionTemplateModal(false)}
          questionTemplates={questionTemplates}
          selectedTemplateId={formData.question_template_id}
          onSelect={handleQuestionTemplateSelect}
        />
      </Container>
    </div>
  );
};

export default CandidateForm;
